import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AFFILIATE, IMG } from "../lib/api";
import AffiliateButton from "../components/AffiliateButton";
import EmailCapture from "../components/EmailCapture";
import { TramFront, Clock, ArrowRight, Check } from "lucide-react";

const itineraries = [
  {
    days: "1-Day Trip",
    subtitle: "The Fast Pass visit",
    summary: "Perfect for London-based visitors who want to squeeze in the main park experience in a single, action-packed day.",
    highlights: [
      "Early train from London St Pancras (7:30am)",
      "Full day at Universal Studios Bedford",
      "Sunset dinner on the Embankment",
      "Late return train to London",
    ],
    cta: "Find Nearby Hotels",
    href: AFFILIATE.booking,
    testId: "itinerary-1-day",
  },
  {
    days: "2-Day Trip",
    subtitle: "The sweet spot",
    summary: "Our most popular itinerary — one full day at the park, one day exploring Bedford's riverside, markets and countryside.",
    highlights: [
      "Day 1 — Universal Studios (full day)",
      "Overnight at a family hotel near the park",
      "Day 2 — Embankment walk, lunch pub crawl",
      "Afternoon boat trip on the Great Ouse",
    ],
    cta: "Book a 2-Night Stay",
    href: AFFILIATE.booking,
    testId: "itinerary-2-day",
  },
  {
    days: "3-Day Trip",
    subtitle: "The complete experience",
    summary: "The unhurried option — Universal in full, Bedford at leisure, plus a Cambridge or Milton Keynes day trip.",
    highlights: [
      "Day 1 — Universal Studios + evening meal",
      "Day 2 — Bedford town, river & Higgins Art Gallery",
      "Day 3 — Day trip to Cambridge (35 min by train)",
      "Three nights at your chosen hotel",
    ],
    cta: "Explore Hotel Options",
    href: AFFILIATE.booking,
    testId: "itinerary-3-day",
  },
];

const PlanTrip = () => {
  useEffect(() => {
    document.title = "Plan Your Trip to Universal Studios Bedford — 1–3 Day Itineraries | Visit Bedford";
  }, []);

  return (
    <div data-testid="plan-trip-page">
      {/* HERO */}
      <section className="relative h-[68vh] min-h-[480px] w-full overflow-hidden">
        <img src={IMG.trainTravel} alt="Train journey" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-site pb-16 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                Plan Your Trip
              </div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[0.95] text-balance mb-5">
                Plan your trip to Universal Studios Bedford.
              </h1>
              <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                Visiting Universal Studios Bedford? Here's how to plan the perfect 1, 2 or 3-day trip — with honest advice from people who live here.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transport section */}
      <section className="bg-[#FAFAFA] py-20 md:py-28">
        <div className="container-site grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">
              Getting there
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-[#0B2C5B] leading-tight mb-4">
              London to Bedford — easier than you think.
            </h2>
            <p className="text-lg text-slate-700">
              Bedford sits on the Thameslink line, meaning fast, frequent and cheap trains direct from central London — no changes required.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: TramFront, title: "From St Pancras", value: "40 minutes", note: "Thameslink fast train every 15 min" },
              { icon: TramFront, title: "From Farringdon", value: "50 minutes", note: "Direct Thameslink via the City" },
              { icon: Clock, title: "From Gatwick Airport", value: "1h 40m", note: "Direct Thameslink — no changes" },
              { icon: Clock, title: "From Luton Airport", value: "20 minutes", note: "East Midlands Railway" },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg transition-all">
                <t.icon className="w-6 h-6 text-[#FF8C00] mb-3" />
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500 mb-1">{t.title}</div>
                <div className="font-serif-display text-2xl font-bold text-[#0B2C5B] mb-1">{t.value}</div>
                <div className="text-sm text-slate-600">{t.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Itinerary cards */}
      <section className="bg-gradient-to-b from-[#FAFAFA] to-[#E6F6FD] py-20 md:py-28">
        <div className="container-site">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2AB7E8] mb-4">Itineraries</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-[#0B2C5B] leading-tight">
              Choose your pace.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {itineraries.map((it, i) => (
              <motion.div
                key={it.days}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={it.testId}
                className="relative bg-white rounded-3xl p-8 md:p-10 shadow-[0_20px_60px_rgba(11,44,91,0.08)] border border-slate-100 hover:-translate-y-1 transition-all duration-500 flex flex-col"
              >
                <div className="absolute -top-4 left-8 bg-[#0B2C5B] text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full">
                  0{i + 1}
                </div>
                <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#FF8C00] mb-3">
                  {it.subtitle}
                </div>
                <h3 className="font-serif-display text-3xl font-bold text-[#0B2C5B] mb-4">{it.days}</h3>
                <p className="text-slate-700 leading-relaxed mb-6">{it.summary}</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {it.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-[#2AB7E8] mt-0.5 flex-shrink-0" /> {h}
                    </li>
                  ))}
                </ul>
                <AffiliateButton href={it.href} variant="primary" testId={`${it.testId}-cta`}>
                  {it.cta}
                </AffiliateButton>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link to hotels */}
      <section className="bg-[#0B2C5B] text-white py-16 md:py-20">
        <div className="container-site grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mb-3">
              Already know your dates? Lock in your hotel.
            </h3>
            <p className="text-white/70 text-lg">
              Hotel prices near the park are already rising for the 2026 opening season. Booking early saves money.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link
              to="/hotels"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold bg-white text-[#0B2C5B] hover:bg-[#FFF7E8] transition"
              data-testid="plan-to-hotels-link"
            >
              Browse Hotels <ArrowRight className="w-4 h-4" />
            </Link>
            <AffiliateButton href={AFFILIATE.getYourGuide} variant="primary" testId="plan-gyg-cta">
              Find Experiences
            </AffiliateButton>
          </div>
        </div>
      </section>

      <EmailCapture source="plan-trip" />
    </div>
  );
};

export default PlanTrip;
